/** Shared primitive types for the combat simulation. */

export type EntityId = number;
/** Network peer id ('local' in solo play). */
export type PeerId = string;

export type Team = 'players' | 'monsters' | 'neutral' | 'red' | 'blue';

export type Element = 'none' | 'fire' | 'water' | 'lightning' | 'earth' | 'holy' | 'dark';

/** Super armor tiers: what a character ignores while a skill is active. */
export type SuperArmor = 'none' | 'paralysis' | 'push' | 'full';
export const SUPER_ARMOR_RANK: Record<SuperArmor, number> = { none: 0, paralysis: 1, push: 2, full: 3 };
export const maxSuperArmor = (a: SuperArmor, b: SuperArmor): SuperArmor =>
  SUPER_ARMOR_RANK[a] >= SUPER_ARMOR_RANK[b] ? a : b;

/** Stagger tiers from skill tooltips, mapped to fixed stagger points (does not scale with attack). */
export type StaggerTier = 'none' | 'low' | 'mid' | 'midHigh' | 'high' | 'max';
export const STAGGER_TIERS: readonly StaggerTier[] = ['none', 'low', 'mid', 'midHigh', 'high', 'max'];
export const STAGGER_POINTS: Record<StaggerTier, number> = {
  none: 0,
  low: 40,
  mid: 80,
  midHigh: 120,
  high: 180,
  max: 300,
};
export const stepStagger = (t: StaggerTier, steps: number): StaggerTier => {
  const i = Math.min(STAGGER_TIERS.length - 1, Math.max(0, STAGGER_TIERS.indexOf(t) + steps));
  return STAGGER_TIERS[i] as StaggerTier;
};

/** Which positional bonus a skill can earn. */
export type Positional = 'none' | 'back' | 'head' | 'both';

/** Monster grades, weakest to strongest. */
export type Grade = 'minion' | 'normal' | 'elite' | 'named' | 'boss' | 'raid';
export const GRADE_RANK: Record<Grade, number> = { minion: 0, normal: 1, elite: 2, named: 3, boss: 4, raid: 5 };
/** Bosses and raid bosses ignore push and hard CC. */
export const isBossGrade = (g: Grade): boolean => GRADE_RANK[g] >= GRADE_RANK.boss;

/** Physical reaction an attack tries to force on its target. */
export type ReactionKind = 'none' | 'flinch' | 'knockback' | 'knockdown' | 'launch' | 'pull';

export interface Reaction {
  kind: ReactionKind;
  /** Displacement in metres for knockback/pull, or launch height. */
  power?: number;
  /** Time spent downed / airborne. */
  duration?: number;
}

export type CastType = 'normal' | 'combo' | 'chain' | 'holding' | 'charge' | 'point' | 'casting' | 'toggle';

export type SkillSlot = 'Q' | 'W' | 'E' | 'R' | 'A' | 'S' | 'D' | 'F';
export const SKILL_SLOTS: readonly SkillSlot[] = ['Q', 'W', 'E', 'R', 'A', 'S', 'D', 'F'];

/** Keys that trigger actions: skill slots, dash, identity, awakening, basic attack. */
export type ActionKey = SkillSlot | 'SPACE' | 'Z' | 'X' | 'V' | 'BASIC' | 'I1' | 'I2' | 'I3' | 'I4';
